import React, { useEffect } from "react";
import styles from "./_deposit-page.module.scss";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AppState } from "../../../Store/rootReducer";
import { useAppDispatch } from "../../../Store/useAppDispatch";
import { clearDownPaymentLink, createDownPaymentLink, loadLibrary, setClient } from "../../../Store/Shared/actions";
import LoadingIndicator from "../../Shared/LoadingIndicator/LoadingIndicator";
import MessageViewer from "../../Controls/MessageViewer/MessageViewer";
import { Button } from "@progress/kendo-react-buttons";
import { Checkbox, CheckboxChangeEvent } from "@progress/kendo-react-inputs";
import {CreditAppScoringResultPage, getTheoreticalPrice, hasValue, toDollars} from "@amsanalytics/react-shared-library";
import {
  areDepositSelectionsWithinLimits,
  buildDownPaymentLinkRequest,
  decodeDepositPageParameters,
  DepositLinkParams,
  getFinancingTermsDisplay,
  getQuoteFailedScreenText
} from "./depositPageUtils";
import {
  getDownPaymentHistories,
  loadCuratedApplicationDetails,
  loadCuratedVehicleDetail,
  setVehicleFilters
} from "../../../Store/CuratedVehicles/actions";
import {Params, useNavigate} from "react-router";
import html2canvas from 'html2canvas-pro';
import {httpFormDataAsync} from "../../../Http/HttpClient";
import {MS_PER_MINUTE} from "@progress/kendo-date-math";
import useThirdPartyChatbot from "../../../Hooks/useThirdPartyChatbot";

interface DepositPageParams extends DepositLinkParams {
  clientId: string;
}

export interface DepositPageProps {
  headerAndFooterHeight: number;
}

// Matches the data-deposit-scroller attribute on the scrolling content container, so the
// screenshot capture can find it in both the live page and the html2canvas clone.
const SCROLLER_SELECTOR = '[data-deposit-scroller]';

interface InfoTileProps {
  label: string;
  colorClassName: string; // sets the tile's accent color (left border and value)
  detail?: string; // a smaller second line under the value
  children: React.ReactNode;
}

// One figure on the confirmation page. Defined outside the page so it is not re-created on every render.
const InfoTile = ({label, colorClassName, detail, children}: InfoTileProps) => (
  <div className={`${styles.tile} ${colorClassName}`}>
    <div className={styles.tileLabel}>{label}</div>
    <div className={styles.tileValueBlock}>
      <div className={styles.tileValue}>{children}</div>
      {detail && <div className={styles.tileDetail}>{detail}</div>}
    </div>
  </div>
);

export default function DepositPage (props: DepositPageProps) {
  const dispatch = useAppDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const clientId = useSelector<AppState, string | null>(appState => appState.shared.clientId);
  const loadingDetail = useSelector((state: AppState) => state.curatedVehicles.loading);
  const selectedVehicleLoaded = loadingDetail.selectedVehicleLoaded;
  const selectedVehicle = useSelector((state: AppState) => state.curatedVehicles.selectedVehicle);
  const isCreatingDownPaymentLink = useSelector<AppState, boolean>(appState => appState.shared.isCreatingDownPaymentLink);
  const createDownPaymentLinkFailed = useSelector<AppState, boolean>(appState => appState.shared.createDownPaymentLinkFailed);
  const applicationDetailsLoadFailed = loadingDetail.applicationDetailsLoadFailed;
  const applicationDetails = useSelector((state: AppState) => state.curatedVehicles.application);
  const downPaymentDetails = useSelector((appState: AppState) => appState.shared.curatedSiteDownPaymentRequestDetails);
  // Term and rate from the quote run while the link was created; part of the captured "accepted terms"
  const financingTerms = getFinancingTermsDisplay(downPaymentDetails?.quoteTerms);
  const [step, setStep] = React.useState(0);
  const [acknowledged, setAcknowledged] = React.useState(false);
  const downPaymentHistories = useSelector((state: AppState) => state.curatedVehicles.downPaymentHistories);

  // Hide (don't unload) the chat widget while the payment iframe is up; it comes back on Back.
  useThirdPartyChatbot(applicationDetails?.chatbotScriptUrl, step === 1);

  // Convert base64 image to File
  const base64ToFile = (base64: string, filename: string, contentType = 'image/jpeg'): File => {
    const byteCharacters = atob(base64.split(',')[1]);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: contentType });
    return new File([blob], filename, { type: contentType });
  };

  // Uploads a screenshot of the confirmation page to the server
  const uploadScreenshot = async (base64Image: string) => {
    const filename = `Deposit_Disclaimer_Confirmation_For_Stock_${selectedVehicle?.stockNumber}.jpeg`;
    const file = base64ToFile(base64Image, filename);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('applicationId', depositPageParams!.applicationId);
      formData.append('downPaymentRequestId', downPaymentDetails!.downPaymentRequestId);
      
      const response = await httpFormDataAsync(
        `DownPayment/upload?clientId=${clientId}`, 
        "POST",
        formData,
        ''
        );
      
      if (response?.ok) {
        console.log('Screenshot uploaded successfully');
      } else {
        console.error('Failed to upload screenshot');
      }
    } catch (error) {
      console.error('Error uploading screenshot:', error);
    }
  };

  const [downPaymentMinutesInterval, setDownPaymentMinutesInterval] = React.useState<number>(2);

  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const captureScreenshot = async (): Promise<string | null> => {
    try {
      const container = rootRef.current;
      if (!container) {
        return null;
      }

      // On mobile the inner content container scrolls, so the terms/vehicle info above the
      // fold would be clipped. Capture at full content height by expanding the cloned DOM
      // (the visible page is untouched). The scroller is found by data attribute rather than
      // by its CSS-module class, whose generated hash can contain selector metacharacters
      // (e.g. "…EELK+") — escaping those needs CSS.escape, which is missing in some WebViews.
      const scroller = container.querySelector<HTMLElement>(SCROLLER_SELECTOR);
      const fullHeight = container.scrollHeight
        + (scroller ? scroller.scrollHeight - scroller.clientHeight : 0);

      const canvas = await html2canvas(container, {
        height: fullHeight,
        windowHeight: fullHeight,
        onclone: (_clonedDocument, clonedContainer) => {
          clonedContainer.style.height = 'auto';
          const clonedScroller = clonedContainer.querySelector<HTMLElement>(SCROLLER_SELECTOR);
          if (clonedScroller) {
            clonedScroller.style.overflowY = 'visible';
            clonedScroller.style.height = 'auto';
          }
        }
      });
      return canvas.toDataURL("image/jpeg", 1.0);
    } catch (error) {
      console.error('Error capturing the confirmation screenshot:', error);
      return null;
    }
  };

  const handleGoToPaymentLink = async () => {
    if (acknowledged) {
      // Wait for the checkbox state to render AND for the tap's synthesized mouse events to
      // be processed before html2canvas blocks the main thread — Kendo's draggable throws if
      // its touch/mouse suppression window expires while the event loop is starved.
      await new Promise(resolve => setTimeout(resolve, 400));
      // Capture before switching steps so the acknowledged step-0 content is what's cloned.
      const image = await captureScreenshot();
      setStep(1);
      // Changing the interval to check every 15 seconds because they could have made a payment any time after the button is clicked
      setDownPaymentMinutesInterval(.25);
      if (image) {
        // Upload in the background; the payment view shouldn't wait on the network.
        uploadScreenshot(image);
      }
    }
  }
  
  // The only deposit route is /:clientId/deposit/:encodedUrl; null when the encoded part is not a deposit link
  const getParams = (params: Readonly<Params<string>>): DepositPageParams | null => {
    const decodedParams = decodeDepositPageParameters(params.encodedUrl);
    return decodedParams && params.clientId
      ? {...decodedParams, clientId: params.clientId}
      : null;
  }

  const [depositPageParams, setDepositPageParams] = React.useState<DepositPageParams | null>(getParams(params));

  // The portal link's slider limits, loaded here too so a refresh or a reopened link can be checked against them
  const applicationDetailsLoaded = !!applicationDetails && !!depositPageParams
    && applicationDetails.applicationId?.toLowerCase() === depositPageParams.applicationId?.toLowerCase();
  const selectionsAreValid = applicationDetailsLoaded
    && areDepositSelectionsWithinLimits(depositPageParams!, applicationDetails!);
  // The link's selections are out of the sliders' ranges or missing (an older link or a hand-edited one), or the server
  // refused them (the limits changed since the page checked them, or it could not create the link)
  const mustReturnToSliders = !!depositPageParams
    && (depositPageParams.monthlyPayment === undefined
      || (applicationDetailsLoaded && !selectionsAreValid)
      || createDownPaymentLinkFailed);
  const isLoading = !selectedVehicleLoaded || isCreatingDownPaymentLink || !selectionsAreValid;

  // Asks for the portal link's limits once per link. Development StrictMode runs this effect twice on mount, and a
  // second request that fails shows its error a second time; Try again asks again on its own.
  const detailsRequestedForRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (depositPageParams?.clientId) {
      if (clientId !== depositPageParams.clientId) {
        dispatch(setClient(depositPageParams.clientId));
      }
      dispatch(loadLibrary(depositPageParams.clientId));
      dispatch(loadCuratedVehicleDetail(depositPageParams.clientId, null, depositPageParams.stockNumber));
      if (!applicationDetailsLoaded && detailsRequestedForRef.current !== depositPageParams.customerPortalLinkId) {
        detailsRequestedForRef.current = depositPageParams.customerPortalLinkId;
        dispatch(loadCuratedApplicationDetails(depositPageParams.clientId, depositPageParams.customerPortalLinkId));
      }
    }
  }, [depositPageParams, dispatch]);

  // Creates the payment link once per deposit link, after its selections pass the limits. Setting the client and
  // restoring the sliders re-render the page, so this compares the link's values; a second link would also run the
  // server's quote a second time.
  const linkRequestedForRef = React.useRef<string | null>(null);
  useEffect(() => {
    const linkKey = depositPageParams ? JSON.stringify(depositPageParams) : null;
    if (!selectionsAreValid || !depositPageParams || linkRequestedForRef.current === linkKey) {
      return;
    }
    linkRequestedForRef.current = linkKey;

    dispatch(createDownPaymentLink(buildDownPaymentLinkRequest(depositPageParams.clientId, depositPageParams)));

    // Keep the sliders where the shopper left them if they go back (loading the portal link resets them to the defaults).
    // The price cap is derived from the two sliders, the same way the filter panel recalculates it when they move;
    // keeping the default one would hide vehicles the shopper's selections can afford.
    if (applicationDetails?.selectedFilters) {
      dispatch(setVehicleFilters({
        ...applicationDetails.selectedFilters,
        downPayment: depositPageParams.downPayment!,
        monthlyPayment: depositPageParams.monthlyPayment!,
        maxPrice: getTheoreticalPrice(
          depositPageParams.monthlyPayment!, applicationDetails.breakevenMonths, depositPageParams.downPayment!, applicationDetails.overhead)
      }));
    }
  }, [selectionsAreValid, depositPageParams, applicationDetails, dispatch]);

  useEffect(() => {
    if (mustReturnToSliders && depositPageParams) {
      navigate(`/${depositPageParams.clientId}/curated/${depositPageParams.customerPortalLinkId}`);
    }
  }, [mustReturnToSliders, depositPageParams, navigate]);

  // Forget this link (and whether it failed) on the way out, so the next deposit page starts clean. Without this the
  // next page would see the previous failure on its first render and send the shopper straight back to the sliders.
  useEffect(() => () => {
    dispatch(clearDownPaymentLink());
  }, [dispatch]);

  // Updates the depositPageParams state when the link changes. The same link keeps the same object, so the effects keyed
  // on it (loading the vehicle, library and limits) do not run a second time after the first render.
  useEffect(() => {
    const nextParams = getParams(params);
    setDepositPageParams(current => JSON.stringify(current) === JSON.stringify(nextParams) ? current : nextParams);
  }, [params]);

  useEffect(() => {
    if (hasValue(depositPageParams?.applicationId) && hasValue(clientId)) {
      // Once when it loads
      dispatch(getDownPaymentHistories(depositPageParams!.applicationId, clientId!));

      // Repeats every X minutes
      let timer = setInterval(async () => {
        dispatch(getDownPaymentHistories(depositPageParams!.applicationId, clientId!));
      }, downPaymentMinutesInterval * MS_PER_MINUTE); // X minutes
      return () => clearInterval(timer);
    }
  }, [depositPageParams?.applicationId, clientId, downPaymentMinutesInterval]);

  // Redirect to the thank you page if they've already made a payment
  if (downPaymentHistories.length > 0 && depositPageParams) {
    navigate(`/${clientId}/payment-thank-you/${depositPageParams?.customerPortalLinkId}`);
  }

  // The vehicle in one compact card, then one slim colored tile per figure. A plain element rather than a component
  // declared in here: a component function recreated on each render is a new type to React, which would remount the
  // cards on every store update instead of updating them in place.
  const vehicleSummaryCards = (
    <div className={styles.vehicleCardsContainer}>
      <div className={`${styles.infoCard} ${styles.vehicleSummary}`}>
        <h3>{selectedVehicle?.year} {selectedVehicle?.make} {selectedVehicle?.model}</h3>
        <div className={styles.vehicleDetails}>
          <div className={styles.detailRow}>
            <strong>Stock #:</strong> {selectedVehicle?.stockNumber}
          </div>
          <div className={styles.detailRow}>
            <strong>VIN:</strong> {selectedVehicle?.vin}
          </div>
        </div>
      </div>

      <div className={styles.tileList}>
        {/* The listed price the shopper saw on the vehicle list; the largest figure on the page, per the dealer */}
        {(selectedVehicle?.price ?? 0) > 0 &&
          <InfoTile label="Price"colorClassName={`${styles.blue} ${styles.featured}`}>
            {toDollars(selectedVehicle!.price)}
          </InfoTile>
        }
        <InfoTile label="Down Payment" colorClassName={styles.purple}>
          {toDollars(depositPageParams?.downPayment)}
        </InfoTile>
        <InfoTile label="Deposit Amount" colorClassName={styles.orange} detail="Your deposit goes towards your total down payment">
          {toDollars(depositPageParams?.depositAmount)}
        </InfoTile>

        {/* The quoted schedule when the server calculated a complete quote; otherwise just the payment the shopper chose */}
        {financingTerms ? (
          <>
            <InfoTile label="APR" colorClassName={styles.indigo}>
              {financingTerms.aprText}
            </InfoTile>
            <InfoTile label="Monthly Payments" colorClassName={styles.green} detail={financingTerms.finalPaymentText}>
              {financingTerms.regularPaymentsText}
            </InfoTile>
          </>
        ) : (
          <InfoTile label="Monthly Payment" colorClassName={styles.green}>
            {toDollars(depositPageParams?.monthlyPayment)}<span className={styles.perMonth}>/mo</span>
          </InfoTile>
        )}
      </div>
    </div>
  );

  const containerStyle: React.CSSProperties = { height: `calc(100vh - ${props.headerAndFooterHeight}px)` };

  // A hand-edited or truncated link: there is no portal link to send the shopper back to
  if (!depositPageParams) {
    return (
      <div className={styles.depositPageContainer} style={containerStyle}>
        <div className={styles.downPaymentContentContainer}>
          <div className={styles.mainContentBox}>
            <h2 className={styles.pageTitle}>This deposit link is not valid</h2>
            <p className={styles.introText}>
              Please open the link your dealer sent you, or pick your vehicle again from your personalized inventory page.
            </p>
          </div>
        </div>
        <MessageViewer />
      </div>
    );
  }

  // The portal link's metadata could not be loaded, so the link's selections cannot be checked; stop waiting and say so
  if (applicationDetailsLoadFailed && !applicationDetailsLoaded) {
    return (
      <div className={styles.depositPageContainer} style={containerStyle}>
        <div className={styles.downPaymentContentContainer}>
          <div className={styles.mainContentBox}>
            <h2 className={styles.pageTitle}>We couldn't load your deposit page</h2>
            <p className={styles.introText}>
              Please try again. If this keeps happening, the link may have expired; your dealer can send you a new one.
            </p>
            <Button
              themeColor={"primary"}
              onClick={() => dispatch(loadCuratedApplicationDetails(depositPageParams.clientId, depositPageParams.customerPortalLinkId))}
            >Try again</Button>
          </div>
        </div>
        <MessageViewer />
      </div>
    );
  }

  // The quote for these selections could not be calculated, so no payment link was created. The shopper gets the
  // configured message (the same result screen as after the credit application) instead of the deposit page.
  if (downPaymentDetails?.quoteFailed && depositPageParams) {
    const quoteFailedText = getQuoteFailedScreenText(applicationDetails?.quoteFailedMessage);
    return (
      <CreditAppScoringResultPage
        headerText={quoteFailedText.headerText}
        resultMessage={quoteFailedText.resultMessage}
        nextActionText={quoteFailedText.nextActionText}
        buttonText={quoteFailedText.buttonText}
        headerAndFooterHeight={props.headerAndFooterHeight}
        onContinue={() => quoteFailedText.buttonUrl
          ? window.location.assign(quoteFailedText.buttonUrl)
          : navigate(`/${depositPageParams.clientId}/curated/${depositPageParams.customerPortalLinkId}`)}
      />
    );
  }

  return isLoading
    ? (
      <LoadingIndicator label="Loading your page. Just a moment..."/>
    )
    : (
      <div ref={rootRef} className={styles.depositPageContainer} style={containerStyle}>
        {downPaymentDetails && (
          <div className={styles.downPaymentContentContainer} data-deposit-scroller="">
            {step === 1 && (
              <div className={styles.iframeContainer}>
                <h3>Deposit Page</h3>
                <iframe
                  src={downPaymentDetails.link}
                  title={downPaymentDetails.paymentProcessorName}
                />
                <div className={styles.backButtonWrapper}>
                  <Button
                    title="Go back to the prevous page"
                    className={styles.backButton}
                    themeColor={"secondary"}
                    onClick={() => {
                      setStep(0);
                    }}
                  >Back</Button>
                </div>
              </div>
            )}
            {step === 0 && (
              <div className={styles.contentWrapper}>
                {/* Left Column - Vehicle Summary Cards (Desktop) */}
                <div className={styles.vehicleCardsDesktop}>
                  {vehicleSummaryCards}
                </div>

                {/* Mobile Version - Vehicle Summary Cards (shown at top on mobile) */}
                <div className={styles.vehicleCardsMobile}>
                  {vehicleSummaryCards}
                </div>

                {/* Right Column - Main Content */}
                <div className={styles.mainContentColumn}>
                  <div className={styles.mainContentBox}>
                    <h2 className={styles.pageTitle}>
                      Next Steps – Let's Make It Official!
                    </h2>

                    <p className={styles.introText}>
                      We're thrilled to help you lock in your new trailer! Here's what's next:
                    </p>

                    <ul className={styles.stepsList}>
                      <li className={styles.stepItem}>
                        <span className={styles.stepBullet}></span>
                        <div className={styles.stepContent}>
                          <strong>{'Place your deposit to officially reserve your trailer and take it off the market. This also lets our team start prepping it just for you!'}</strong>
                        </div>
                      </li>

                      <li className={styles.stepItem}>
                        <span className={styles.stepBullet}></span>
                        <div className={styles.stepContent}>
                          Your deposit will <strong>go straight toward your down payment</strong>, and you'll have <strong>30 days to complete the process and pick up your new home on wheels.</strong>
                        </div>
                      </li>

                      <li className={`${styles.stepItem} ${styles.warningStep}`}>
                        <span className={`${styles.stepBullet} ${styles.warningBullet}`}></span>
                        <div className={styles.stepContent}>
                          <strong className={styles.warningText}>Heads up:</strong> All deposits are <strong>non-refundable</strong>, so be sure you're ready to move forward and make this yours!
                        </div>
                      </li>

                      <li className={styles.stepItem}>
                        <span className={styles.stepBullet}></span>
                        <div className={styles.stepContent}>
                          Once your deposit is in, a team member will reach out shortly to <strong>schedule your pickup day and time.</strong>
                        </div>
                      </li>

                      <li className={styles.stepItem}>
                        <span className={styles.stepBullet}></span>
                        <div className={styles.stepContent}>
                          Before pickup, we'll just need proof of <strong>3 months of prepaid insurance</strong> on your trailer.
                        </div>
                      </li>
                    </ul>

                    <p className={styles.contactBox}>
                      If you have any questions or need help with next steps, just give us a shout—we're here and excited to help you hit the road! 🚐✨
                    </p>
                  </div>
                  <div className={styles.actionContainer}>
                    <Checkbox
                      value={acknowledged}
                      label="I understand the deposit is non-refundable and will be applied to my down payment."
                      onChange={(event: CheckboxChangeEvent) => {
                        if (event.value) {
                          setAcknowledged(true);
                        } else {
                          setAcknowledged(false);
                        }
                      }}
                    />
                    <div className={styles.actionButtons}>
                      {/* The vehicle list keeps the shopper's sliders (restored from the link when the page was reloaded) */}
                      <Button
                        className={styles.depositButton}
                        themeColor={"secondary"}
                        title="Go back to the vehicle list"
                        onClick={() => navigate(`/${depositPageParams!.clientId}/curated/${depositPageParams!.customerPortalLinkId}`)}
                      >Back to Vehicles</Button>
                      <Button
                        className={styles.depositButton}
                        themeColor={"primary"}
                        disabled={!acknowledged}
                        title={`Go to the ${downPaymentDetails.paymentProcessorName} deposit page`}
                        onClick={handleGoToPaymentLink}
                      >Go to Deposit</Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <MessageViewer />
      </div>
    );
}